/**
 * Ending a meeting stops its recording (#15946) — the client half.
 *
 * Recording and ending are one switch. The server cannot close a microphone,
 * so the browser that owns a capture stops it: the room's End meeting, after
 * the meeting is DONE, and every other viewer's tab when the room's refresh
 * shows the meeting ended (`MeetingEndedRedirect`). Both call
 * {@link stopCaptureForEndedMeeting}; the recording bar host registers the
 * stopper, which settles a capture running into THAT meeting through Stop's
 * own path (`stopRecordingFromBar`) and leaves any other capture alone.
 *
 * A registry rather than an import because the bar is app chrome and the
 * room must not depend on it: with no bar mounted there is nothing to stop,
 * and the call resolves at once.
 *
 * Client-safe: no Prisma, no server imports.
 */

type MeetingEndCaptureStopper = (meetingId: string) => Promise<unknown>

let stopper: MeetingEndCaptureStopper | null = null

/** The bar host's registration. Returns its own unregistration. */
export function registerMeetingEndCaptureStopper(
  fn: MeetingEndCaptureStopper,
): () => void {
  stopper = fn
  return () => {
    if (stopper === fn) stopper = null
  }
}

/**
 * Stop a capture running into `meetingId`, if this tab owns one, and wait
 * for its settle. Never throws: the meeting has ended either way, and a
 * capture that could not be settled is finished by the abandoned-capture
 * sweep.
 */
export async function stopCaptureForEndedMeeting(
  meetingId: string,
): Promise<void> {
  if (stopper === null) return
  try {
    await stopper(meetingId)
  } catch {
    // The settle path logs its own failures.
  }
}
